import { useState } from "react";
import { Play, X } from "lucide-react";

/**
 * Homepage "How we print" explainer video block.
 *
 * Renders a click-to-play poster (cheap, no autoplay) that opens a modal
 * with the actual `<video>` element on demand. Emits a VideoObject JSON-LD
 * tag for Google video search / rich results.
 *
 * The video file is self-hosted at `/api/static/video/explainer.mp4` so
 * we control caching + bandwidth (no third-party CDN dependency that can
 * expire). Poster image is exported from the same source.
 */
const VIDEO_SRC = "/api/media/explainer.mp4";
const VIDEO_POSTER = "/api/media/explainer-poster.jpg";
const VIDEO_DURATION_ISO = "PT53S"; // 53-second clip (probed from MP4 mvhd atom)
const VIDEO_TITLE = "How Toronto Custom T-Shirts Prints Your Apparel";
const VIDEO_DESC =
  "A short look at how Toronto Custom T-Shirts turns your artwork into finished custom t-shirts, hoodies and apparel — from upload to print to local pickup or shipping across the GTA.";
const VIDEO_UPLOAD_DATE = "2026-05-20";

const VIDEO_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "VideoObject",
  "name": VIDEO_TITLE,
  "description": VIDEO_DESC,
  "thumbnailUrl": "https://torontocustomtshirts.ca/api/media/explainer-poster.jpg",
  "uploadDate": VIDEO_UPLOAD_DATE,
  "duration": VIDEO_DURATION_ISO,
  "contentUrl": "https://torontocustomtshirts.ca" + VIDEO_SRC,
  "embedUrl": "https://torontocustomtshirts.ca/#explainer-video",
  "publisher": {
    "@type": "Organization",
    "name": "Toronto Custom T-Shirts",
    "logo": {
      "@type": "ImageObject",
      "url": "https://torontocustomtshirts.ca/logo.png",
    },
  },
};

export default function ExplainerVideo() {
  const [open, setOpen] = useState(false);

  return (
    <section
      id="explainer-video"
      className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24"
      data-testid="explainer-video-section"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(VIDEO_SCHEMA) }}
      />

      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <div className="font-mono text-[11px] tracking-widest text-[var(--tc-red)] mb-3">
            / WATCH · UNDER A MINUTE
          </div>
          <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.85]">
            See It<br />Printed Live.
          </h2>
          <p className="font-body text-zinc-300 text-lg mt-6 max-w-md">
            From your file to a folded, packed garment — watch the full process inside our Mississauga studio.
            DTF, embroidery, signage &amp; wraps. No agency middlemen. No surprise costs.
          </p>
          <button
            onClick={() => setOpen(true)}
            data-testid="explainer-video-play-cta"
            className="btn-tc-primary mt-8 inline-flex items-center gap-3"
          >
            <Play size={18} fill="currentColor" /> Watch Video
          </button>
        </div>

        <div className="lg:col-span-7">
          <button
            onClick={() => setOpen(true)}
            data-testid="explainer-video-poster"
            className="group relative w-full aspect-video overflow-hidden border border-[#222] hover:border-[var(--tc-red)] transition-colors block"
            aria-label="Play explainer video"
          >
            <img
              src={VIDEO_POSTER}
              alt={VIDEO_TITLE}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 transition-opacity"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/30 to-[var(--tc-red)]/20" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 lg:w-24 lg:h-24 bg-[var(--tc-red)] flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl shadow-[var(--tc-red)]/40">
                <Play size={36} fill="white" className="text-white translate-x-[2px]" />
              </div>
            </div>
            <div className="absolute bottom-4 left-4 font-mono text-[10px] tracking-widest text-white/80">
              / EXPLAINER · 0:53
            </div>
          </button>
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/95 backdrop-blur"
          onClick={() => setOpen(false)}
          data-testid="explainer-video-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setOpen(false)}
              data-testid="explainer-video-close"
              className="absolute -top-12 right-0 text-zinc-400 hover:text-white p-2"
              aria-label="Close video"
            >
              <X size={28} />
            </button>
            <video
              src={VIDEO_SRC}
              controls
              autoPlay
              playsInline
              poster={VIDEO_POSTER}
              className="w-full aspect-video bg-black border border-[#222]"
              data-testid="explainer-video-player"
            />
          </div>
        </div>
      )}
    </section>
  );
}
